/**
 * 인증이 필요한 API 호출을 위한 유틸리티 함수
 * - credentials: 'include' 자동 추가 (Supabase sb-*-auth-token 쿠키 전송)
 * - 401 에러 시 딸깍 로그인 페이지로 리다이렉트
 * - 403 에러 처리
 */

const DDALKKAK_LOGIN_URL = 'https://xn--il0b54k.net/auth/login';

/**
 * 인증 실패 시 딸깍 로그인으로 리다이렉트
 */
const handleAuthFailure = (errorData) => {
  console.error('❌ 인증 실패:', errorData);
  alert(errorData.message || '세션이 만료되었습니다. 딸깍에서 다시 로그인해주세요.');
  window.location.href = errorData.login_url || DDALKKAK_LOGIN_URL;
};

/**
 * 인증이 필요한 API 호출
 * @param {string} url - API URL
 * @param {Object} options - fetch options
 * @returns {Promise<Response>}
 */
export const fetchWithAuth = async (url, options = {}) => {
  try {
    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (response.status === 401) {
      const errorData = await response.json().catch(() => ({ error: '인증 실패' }));
      handleAuthFailure(errorData);
      throw new Error(errorData.message || '인증이 만료되었습니다.');
    }

    if (response.status === 403) {
      const errorData = await response.json().catch(() => ({ error: '권한 없음' }));
      console.error('❌ 권한 없음:', errorData);
      alert(errorData.message || '권한이 없습니다.');
      throw new Error(errorData.message || '권한이 없습니다.');
    }

    if (response.status === 429) {
      const errorData = await response.json().catch(() => ({ error: 'Rate Limit 초과' }));
      console.error('❌ Rate Limit 초과:', errorData);
      const retryAfter = response.headers.get('Retry-After');
      const retryMessage = retryAfter ? `${retryAfter}초 후` : '잠시';
      alert(`${errorData.message}\n${retryMessage} 다시 시도해주세요.`);
      throw new Error(errorData.message || '요청 횟수 제한을 초과했습니다.');
    }

    return response;

  } catch (error) {
    console.error('❌ API 호출 오류:', error);
    throw error;
  }
};

/**
 * FormData를 사용하는 API 호출 (파일 업로드용)
 * @param {string} url - API URL
 * @param {Object} options - fetch options
 * @returns {Promise<Response>}
 */
export const fetchWithAuthFormData = async (url, options = {}) => {
  try {
    const { headers, ...restOptions } = options;

    const response = await fetch(url, {
      ...restOptions,
      credentials: 'include'
    });

    if (response.status === 401) {
      const errorData = await response.json().catch(() => ({ error: '인증 실패' }));
      handleAuthFailure(errorData);
      throw new Error(errorData.message || '인증이 만료되었습니다.');
    }

    if (response.status === 403) {
      const errorData = await response.json().catch(() => ({ error: '권한 없음' }));
      console.error('❌ 권한 없음:', errorData);
      alert(errorData.message || '권한이 없습니다.');
      throw new Error(errorData.message || '권한이 없습니다.');
    }

    if (response.status === 429) {
      const errorData = await response.json().catch(() => ({ error: 'Rate Limit 초과' }));
      console.error('❌ Rate Limit 초과:', errorData);
      const retryAfter = response.headers.get('Retry-After');
      const retryMessage = retryAfter ? `${retryAfter}초 후` : '잠시';
      alert(`${errorData.message}\n${retryMessage} 다시 시도해주세요.`);
      throw new Error(errorData.message || '요청 횟수 제한을 초과했습니다.');
    }

    return response;

  } catch (error) {
    console.error('❌ API 호출 오류:', error);
    throw error;
  }
};
